import React, { useContext, useMemo } from "react";
import { Button, Drawer, Heading, Table } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { getColumnCell } from "@common/utils/table.util";
import { DayRowData } from "@modules/booking/context/types";
import { Classroom } from "@models/ISchema";
import { BookingContext } from "@modules/booking/context/BookingContext";
import { getDateKey, getTwoDigitsTime } from "@utils/time";
import { useTranslation } from "react-i18next";
import { useLayoutStore } from "@context/useLayoutStore";

interface CoverageResumeDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const CoverageResumeDrawer = ({ isOpen, onClose }: CoverageResumeDrawerProps) => {
  const { t } = useTranslation();
  const { state } = useContext(BookingContext);
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);

  const selectedDatesMap = useMemo(() => {
    if (!isOpen) return [];

    const dates = new Map<
      string,
      {
        classroom: Classroom | null;
        date: DayRowData;
        dateStr: string;
      }
    >();

    state?.search?.dateTimeFormatted.forEach(date => {
      const dateStr = getDateKey(
        date?.day?.dateValue,
        date?.start?.value?.value,
        date?.end?.value?.value,
      );

      dates.set(dateStr, {
        classroom: null,
        date: date,
        dateStr,
      });
    });

    state?.search?.selectedClassrooms.forEach(selectedClassroom => {
      selectedClassroom?.dates.forEach(date => {
        const dateStr = getDateKey(
          date?.day?.dateValue,
          date?.start?.value?.value,
          date?.end?.value?.value,
        );
        const dateMap = dates?.get(dateStr);

        if (dateMap) {
          dates.set(dateStr, {
            classroom: selectedClassroom?.classroom,
            date: dateMap?.date,
            dateStr: dateMap?.dateStr,
          });
        }
      });
    });

    // Sort so that dates with a classroom assigned come first
    return Array.from(dates?.values()).sort((a, b) => {
      if (a.classroom && !b.classroom) {
        return -1;
      }

      if (!a.classroom && b.classroom) {
        return 1;
      }

      return 0;
    });
  }, [isOpen, state?.search?.dateTimeFormatted, state?.search?.selectedClassrooms]);

  return (
    <Drawer
      title={<Heading type="h3">{t("booking.booking-request.drawer-resume.title")}</Heading>}
      isOpen={isOpen}
      onClose={onClose}
      heightGap={visibleHeaderPixels}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("booking.booking-request.actions.close")}
        </Button>
      }
      closeOnOverlayClick
    >
      <Table
        data={selectedDatesMap}
        columns={[
          {
            header: t("booking.booking-request.drawer-resume.day"),
            key: "day",
            renderCell: (row: any) => getColumnCell(row.date.day.dateValue || "-"),
            desktopOptions: {
              headCellStyles: {
                width: "112px",
              },
            },
          },
          {
            header: t("booking.booking-request.drawer-resume.time"),
            key: "time",
            renderCell: (row: any) => {
              const [startHour, startMinute] = getTwoDigitsTime(row?.date?.start?.value?.value);
              const [endHour, endMinute] = getTwoDigitsTime(row?.date?.end?.value?.value);
              const time = `${startHour}:${startMinute} - ${endHour}:${endMinute}`;

              return getColumnCell(time);
            },
            desktopOptions: {
              headCellStyles: {
                width: "120px",
              },
            },
          },
          {
            header: t("booking.booking-request.drawer-resume.classroom"),
            key: "classroom",
            renderCell: (row: any) => getColumnCell(row.classroom?.name || "-"),
            desktopOptions: {
              headCellStyles: {
                width: "200px",
              },
            },
          },
          {
            header: t("booking.booking-request.drawer-resume.coverage"),
            key: "coverage",
            renderCell: (row: any) => (
              <Icon
                name={!!row?.classroom ? "circle-check" : "alert-triangle"}
                color={!!row?.classroom ? "success-50" : "warning-50"}
                filled
              />
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default CoverageResumeDrawer;
